import { useCallback, useEffect, useState } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Check,
  Cloud,
  Loader2,
  MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/shared/empty-state";
import { useAutosave, type SaveStatus } from "@/hooks/use-autosave";
import { formatApiError } from "@/lib/format-api-error";
import { useJourney } from "./use-journey";
import { useJourneyAssignments } from "./use-journey-assignments";
import { useArchiveJourney } from "./use-archive-journey";
import { useUpdateJourney } from "./use-update-journey";
import { JourneyTasksTab } from "./journey-tasks-tab";
import { JourneyAssignmentsTab } from "./journey-assignments-tab";

// Phase 7 sub-bucket 14 — admin journey builder shell. Two tabs (tasks /
// assignments) keyed by ?tab=. The legacy detail page had five tabs
// (configuration / partners / in_progress / done / to_do) — the three progress
// slices were derivable filters of the same journey_task_progress data, so
// the SPA folds them into the Assignments tab v1. Per-task progress detail
// already lives on the partner-side page; the admin Assignments tab surfaces
// the X/Y aggregate per partner row.
//
// Header pattern mirrors sub-bucket 12's tier program editor: inline name +
// description autosave (Pattern B via useAutosave), archive/restore actions
// in a dropdown, save-status pill.

type Tab = "tasks" | "assignments";

const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: "tasks", label: "Tasks" },
  { key: "assignments", label: "Assignments" },
];

function normalizeTab(raw: string | null): Tab {
  if (raw === "tasks" || raw === "assignments") return raw;
  return "tasks";
}

export function JourneyBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = normalizeTab(searchParams.get("tab"));

  if (!id) return <Navigate to="/admin/journeys" replace />;

  return (
    <JourneyBuilderInner
      journeyId={id}
      tab={tab}
      onTabChange={(next) => {
        const sp = new URLSearchParams(searchParams);
        sp.set("tab", next);
        setSearchParams(sp, { replace: true });
      }}
    />
  );
}

function JourneyBuilderInner({
  journeyId,
  tab,
  onTabChange,
}: {
  journeyId: string;
  tab: Tab;
  onTabChange: (next: Tab) => void;
}) {
  const query = useJourney(journeyId);
  const assignmentsQuery = useJourneyAssignments(journeyId);
  const [headerStatus, setHeaderStatus] = useState<SaveStatus>("idle");

  const handleHeaderStatus = useCallback(
    (s: SaveStatus) => setHeaderStatus(s),
    [],
  );

  if (query.isLoading) {
    return <BuilderSkeleton />;
  }
  if (query.isError || !query.data) {
    return (
      <EmptyState
        icon={ArrowLeft}
        title="Couldn't load journey"
        description={
          query.error?.code === "not_found"
            ? "This journey has been archived or deleted."
            : (query.error?.message ?? "Try refreshing.")
        }
        action={
          <Link to="/admin/journeys" className="text-sm text-accent hover:underline">
            ← Back to journeys
          </Link>
        }
      />
    );
  }

  const detail = query.data;
  const tasksCount = detail.tasks.length;
  const assignmentsCount =
    assignmentsQuery.data?.length ?? detail.assignment_count;

  return (
    <div>
      <BuilderHeader
        journey={detail.journey}
        saveStatus={headerStatus}
        onSaveStatusChange={handleHeaderStatus}
      />

      <TabsNav
        active={tab}
        onChange={onTabChange}
        tasksCount={tasksCount}
        assignmentsCount={assignmentsCount}
      />

      <div role="tabpanel" className="mt-4">
        {tab === "tasks" && (
          <JourneyTasksTab
            journeyId={journeyId}
            tasks={detail.tasks}
          />
        )}
        {tab === "assignments" && (
          <JourneyAssignmentsTab journeyId={journeyId} />
        )}
      </div>
    </div>
  );
}

function BuilderHeader({
  journey,
  saveStatus,
  onSaveStatusChange,
}: {
  journey: { id: string; name: string; description: string | null; is_archived: boolean };
  saveStatus: SaveStatus;
  onSaveStatusChange: (status: SaveStatus) => void;
}) {
  const navigate = useNavigate();
  const archive = useArchiveJourney();
  const updateJourney = useUpdateJourney();

  const [name, setName] = useState(journey.name);
  const [description, setDescription] = useState(journey.description ?? "");

  // Sync local state when the server-driven cache changes (e.g. after an
  // optimistic-rollback or a refetch lands).
  useEffect(() => {
    setName(journey.name);
  }, [journey.name]);
  useEffect(() => {
    setDescription(journey.description ?? "");
  }, [journey.description]);

  const flushPatch = useCallback(
    async (patch: { name?: string; description?: string | null }) => {
      await updateJourney.mutateAsync({ id: journey.id, patch });
    },
    [journey.id, updateJourney],
  );

  const nameAutosave = useAutosave<string>({
    onFlush: async (next) => {
      const trimmed = next.trim();
      if (!trimmed) return;
      await flushPatch({ name: trimmed });
    },
    wait: 600,
  });

  const descriptionAutosave = useAutosave<string>({
    onFlush: async (next) => {
      const trimmed = next.trim();
      await flushPatch({ description: trimmed ? trimmed : null });
    },
    wait: 600,
  });

  // Propagate the aggregate up to the page-level indicator.
  useEffect(() => {
    if (nameAutosave.status === "error" || descriptionAutosave.status === "error") {
      onSaveStatusChange("error");
    } else if (
      nameAutosave.status === "saving" ||
      descriptionAutosave.status === "saving"
    ) {
      onSaveStatusChange("saving");
    } else if (
      nameAutosave.status === "saved" ||
      descriptionAutosave.status === "saved"
    ) {
      onSaveStatusChange("saved");
    } else {
      onSaveStatusChange("idle");
    }
  }, [nameAutosave.status, descriptionAutosave.status, onSaveStatusChange]);

  function onNameChange(next: string) {
    setName(next);
    nameAutosave.queue(next);
  }

  function onDescriptionChange(next: string) {
    setDescription(next);
    descriptionAutosave.queue(next);
  }

  function handleArchive() {
    archive.mutate(journey.id, {
      onSuccess: () => {
        toast.success("Journey archived");
        navigate("/admin/journeys");
      },
      onError: (err) => toast.error(formatApiError(err, "Failed to archive")),
    });
  }

  function handleRestore() {
    updateJourney.mutate(
      { id: journey.id, patch: { is_archived: false } },
      {
        onSuccess: () => toast.success("Journey restored"),
        onError: (err) => toast.error(formatApiError(err, "Failed to restore")),
      },
    );
  }

  const pending = archive.isPending || updateJourney.isPending;

  return (
    <div className="mb-6">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <Link
          to="/admin/journeys"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          All journeys
        </Link>
        <div className="flex items-center gap-2">
          <SaveIndicator state={saveStatus} />
          {journey.is_archived && (
            <span className="inline-flex items-center gap-1 rounded-full border border-border/70 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              <Archive className="h-3 w-3" /> Archived
            </span>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="outline" disabled={pending}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {journey.is_archived ? (
                <DropdownMenuItem onClick={handleRestore} disabled={pending}>
                  <ArchiveRestore className="mr-2 h-3.5 w-3.5" /> Restore
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={handleArchive} disabled={pending}>
                  <Archive className="mr-2 h-3.5 w-3.5" /> Archive
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Input
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        placeholder="Journey name"
        maxLength={200}
        className="border-none bg-transparent px-0 font-display text-2xl font-semibold tracking-tight focus-visible:ring-0"
      />
      <Textarea
        value={description}
        onChange={(e) => onDescriptionChange(e.target.value)}
        placeholder="Add a short description…"
        maxLength={500}
        rows={2}
        className="mt-1 max-w-2xl resize-none border-none bg-transparent px-0 text-sm text-muted-foreground focus-visible:ring-0"
      />
    </div>
  );
}

function SaveIndicator({ state }: { state: SaveStatus }) {
  if (state === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving…
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-status-active">
        <Check className="h-3 w-3" />
        Saved
      </span>
    );
  }
  if (state === "error") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-destructive">
        Save failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/70">
      <Cloud className="h-3 w-3" />
      Auto-save on
    </span>
  );
}

function TabsNav({
  active,
  onChange,
  tasksCount,
  assignmentsCount,
}: {
  active: Tab;
  onChange: (next: Tab) => void;
  tasksCount: number;
  assignmentsCount: number;
}) {
  return (
    <div className="flex items-center gap-0.5 border-b border-border/70">
      {TABS.map((t) => {
        const isActive = active === t.key;
        const count = t.key === "tasks" ? tasksCount : assignmentsCount;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={`relative inline-flex h-9 items-center gap-1.5 border-b-2 px-3 text-sm font-medium transition-colors ${
              isActive
                ? "border-accent text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            role="tab"
            aria-selected={isActive}
          >
            {t.label}
            <span className="text-[11px] tabular-nums text-muted-foreground/80">
              ({count})
            </span>
          </button>
        );
      })}
    </div>
  );
}

function BuilderSkeleton() {
  return (
    <div>
      <div className="mb-6 space-y-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
      </div>
      <Skeleton className="h-9 w-full" />
      <Skeleton className="mt-4 h-64 w-full rounded-xl" />
    </div>
  );
}
